import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

// Components
import Header from "./Header";
import { useTheme } from "@mui/material/styles";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import LoadingButton from "../utils/LoadingButton";
import QrCode from "../modals/QrCode";
import ConfirmationModal from "../utils/ConfirmationModal";

// Context
import { useAuth } from "../../contexts/AuthContext";

// Supabase
import supabase from "../../supabaseClient";

const WhatsApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [assignedServer, setAssignedServer] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [whatsAppSessionStatus, setWhatsAppSessionStatus] = useState("Loading");
  const [showQrModal, setShowQrModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
  const [firstTime, setFirstTime] = useState(false); // To open the QR code if it's unlinked only once
  const fromSidebar = location.pathname.includes("whatsapp");

  // Context
  const { teamId, profileId } = useAuth();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    const fetchWhatsAppSessionStatus = async () => {
      if (!profileId) return;

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select(
          `
            assigned_server,
            countrycode_id(
              country_code
            ),
            phone
          `
        )
        .single();

      if (profileError) {
        showNotification(
          t("whatsApp.errorFetchingProfile", { message: profileError.message }),
          "error"
        );
        return;
      }

      setAssignedServer(profileData.assigned_server);
      setPhoneNumber(
        `${profileData.countrycode_id.country_code}${profileData.phone}`
      );

      const { data: contactsData, error: contactsError } = await supabase
        .from("contacts")
        .select("id");

      try {
        setWhatsAppSessionStatus("Loading");
        const response = await fetch(
          `https://getsessionstatus-tiph5o4x3q-uc.a.run.app/?profileId=${profileId}&baseUrl=${profileData.assigned_server}`
        );

        const data = await response.json();

        setWhatsAppSessionStatus(
          data.success && data.message === "session_connected"
            ? "Linked"
            : "Unlinked"
        );

        if (
          data.success &&
          data.message === "session_connected" &&
          !contactsError &&
          !contactsData?.length
        ) {
          // Means they are connected, and have no contacts. Let's import their WhatsApp contacts automatically
          const body = {
            teamId: teamId,
            profileId: profileId,
            baseUrl: profileData.assigned_server,
          };

          await fetch(
            "https://importwhatsappcontacts-tiph5o4x3q-uc.a.run.app",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(body),
            }
          );

          navigate("/dashboard/messages?runTutorial=true");
          return;
        }

        setFirstTime(true);
        if (
          !firstTime &&
          (!data.success || data.message !== "session_connected")
        ) {
          // Show the QR modal if session it's unlinked and the user just opened the page
          setShowQrModal(true);
        } else if (
          !fromSidebar &&
          data.message === "session_connected" &&
          window.location.pathname === "/dashboard"
        ) {
          // Means that they opened from app.getkoubi.com, not from sidebar. If session is linked, take them directly to `/messages`
          navigate("/dashboard/messages");
        }
      } catch (error) {
        showNotification(
          t("whatsApp.errorFetchingStatus", { message: error.message }),
          "error"
        );
        setWhatsAppSessionStatus("Unlinked");
      }
    };

    fetchWhatsAppSessionStatus();
  }, [profileId, showQrModal]);

  const handleAction = async (
    url,
    successMessage,
    errorMessage,
    finalStatus = null
  ) => {
    setIsLoading(true);

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        showNotification(successMessage, "success");
        if (finalStatus) setWhatsAppSessionStatus(finalStatus);
      } else {
        showNotification(`${errorMessage}: ${data.message}`, "error");
      }
    } catch (error) {
      showNotification(`${errorMessage}: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlinkWhatsApp = () => {
    trackEvent("whatsapp-unlink_clicked");
    setShowUnlinkConfirmation(true);
  };

  const handleUnlinkWhatsAppConfirmed = () => {
    trackEvent("whatsapp-unlink_confirmed");
    setShowUnlinkConfirmation(false);

    handleAction(
      `https://terminatesession-tiph5o4x3q-uc.a.run.app/?profileId=${profileId}&baseUrl=${assignedServer}`,
      t("whatsApp.sessionTerminated"),
      t("whatsApp.errorUnlinking"),
      "Unlinked"
    );
  };

  const getWhatsAppSessionStatus = (status) => {
    switch (status) {
      case "Linked":
        return {
          text: t("whatsApp.sessionLinked"),
          icon: <CheckCircleIcon color="success" />,
        };
      case "Unlinked":
        return {
          text: t("whatsApp.sessionUnlinked"),
          icon: <CancelIcon color="error" />,
        };
      default:
        return {
          text: t("whatsApp.connecting"),
          icon: <CircularProgress size={24} />,
        };
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1200px", mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title={t("whatsApp.title")} subtitle={t("whatsApp.subtitle")} />
      </Box>

      <Box
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          p: { xs: 3, md: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: "600px"
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          fontWeight="bold"
          display="flex"
          alignItems="center"
          mb="24px"
          sx={{ color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d" }}
        >
          {getWhatsAppSessionStatus(whatsAppSessionStatus).text}&nbsp;&nbsp;
          {getWhatsAppSessionStatus(whatsAppSessionStatus).icon}
        </Typography>

        {whatsAppSessionStatus === "Linked" ? (
          <LoadingButton
            variant="outlined"
            color="error"
            onClick={handleUnlinkWhatsApp}
            loading={isLoading}
            sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, px: 4, py: 1.5 }}
          >
            {t("whatsApp.unlinkWhatsApp")}
          </LoadingButton>
        ) : (
          whatsAppSessionStatus === "Unlinked" && (
            <LoadingButton
              variant="contained"
              onClick={() => setShowQrModal(true)}
              loading={isLoading}
              sx={{
                borderRadius: "8px",
                px: 4,
                py: 1.5,
                fontWeight: "bold",
                background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                color: "#141b2d",
                boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                "&:hover": { background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)", color: "#fff" }
              }}
            >
              {t("whatsApp.linkWhatsApp")}
            </LoadingButton>
          )
        )}
      </Box>

      <QrCode
        open={showQrModal}
        onClose={() => setShowQrModal(false)}
        profileId={profileId}
        assignedServer={assignedServer}
        whatsAppSessionStatus={whatsAppSessionStatus}
        phoneNumber={phoneNumber}
      />

      <ConfirmationModal
        open={showUnlinkConfirmation}
        onClose={() => setShowUnlinkConfirmation(false)}
        onConfirm={handleUnlinkWhatsAppConfirmed}
        title={t("whatsApp.unlinkConfirmationTitle")}
        message={t("whatsApp.unlinkConfirmationMessage")}
      />
    </Box>
  );
};

export default WhatsApp;
